/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 3rd 2022, 10:09:58 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import * as React from "react";
import Box from "@mui/material/Box";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import { COLORSCHEME } from "theme/styles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Settings from "@mui/icons-material/Settings";
import Launch from "@mui/icons-material/Launch";
import ShareIcon from "@mui/icons-material/Share";
import Paper from "@mui/material/Paper";
import {
  ProcessDefinitionGeneralInfoForm,
  ProcessDefinitionStatusChange,
  ShareServiceFormsLinksDialog,
} from "service-builder";
import { useServiceBuilderContext } from "service-builder/providers";
import { CircularProgress } from "@mui/material";
import { OSAvatar, useToggleState } from "ui-widgets";
import { ProcessDefinition } from "lib/schemas";
import { FirstSideBar } from "theme/components";
import { ProcessDefinitionStatusSlider } from "service-builder/components/process-definition/ProcessDefinitionStatusSlider";
import { StatusUIProperties } from "service-builder/components/process-definition/ProcessDefinitionUIProperties";

const LeftPanelToolbarNav = styled(List)<{ component?: React.ElementType }>({
  "& .MuiListItemButton-root": {
    paddingLeft: 24,
    paddingRight: 24,
  },
  "& .MuiListItemIcon-root": {
    minWidth: 0,
    marginRight: 16,
  },
  "& .MuiSvgIcon-root": {
    fontSize: 20,
  },
});

interface ServiceBuilderLeftPanelProps {
  children: React.ReactNode;
}

export const ServiceBuilderLeftPanel = ({ children: toolbarItems }: ServiceBuilderLeftPanelProps) => {
  const {
    open: processDefinitionGeneralInfoPopupOpenState,
    handleClose: closeProcessDefinitionGeneralInfoPopupFn,
    handleOpen: openProcessDefinitionGeneralInfoPopupFn,
  } = useToggleState();

  const {
    processDefinitionContext: { currentProcessDefinition, isFetchingProcessDefinition },
  } = useServiceBuilderContext();

  const {
    handleClose: closeChangeStatusFn,
    handleOpen: openChangeStatusFn,
    open: changeStatusOpenState,
  } = useToggleState();

  return (
    <>
      <Box className="os-service-builder-left-panel">
        <FirstSideBar />
        {currentProcessDefinition?.id && (
          <>
            <ProcessDefinitionGeneralInfoForm
              processDefinitionGeneralInfoFormOpenState={processDefinitionGeneralInfoPopupOpenState}
              closeProcessDefinitionGeneralInfoForm={closeProcessDefinitionGeneralInfoPopupFn}
              processDefinition={currentProcessDefinition}
            />
            <ProcessDefinitionStatusChange handleClose={closeChangeStatusFn} open={changeStatusOpenState} />
          </>
        )}
        {/* Keep the pop up form outside of the theme provider below so it can maintain its look and feel */}
        <ThemeProvider
          theme={createTheme({
            components: {
              MuiListItemButton: {
                defaultProps: {
                  disableTouchRipple: true,
                },
              },
            },
            palette: {
              mode: "light",
              primary: { main: COLORSCHEME.secondary },
              background: { paper: "rgb(255, 255, 255)" },
            },
          })}
        >
          <Paper elevation={0}>
            <LeftPanelToolbarNav className="os-sb-left-nav" component="nav" disablePadding>
              <div className="os-sb-left-nav-topitem os-sb-left-nav-title">
                <LeftPanelHeader
                  serviceTitle={currentProcessDefinition?.title}
                  serviceLogo={currentProcessDefinition?.service_logo}
                  isFetchingProcessDefinition={isFetchingProcessDefinition}
                />
              </div>
              <div className="os-sb-left-nav-topitem">
                <ServiceDetailsMenu openProcessDefinitionGeneralInfoPopupFn={openProcessDefinitionGeneralInfoPopupFn} />
              </div>
              <div className="os-sb-left-nav-topitem">
                <OpenStaffViewMenu url={currentProcessDefinition?.service_dashboard_url} />
              </div>
              <div className="os-sb-left-nav-topitem">
                {currentProcessDefinition?.id ? (
                  <ShareFormLinksMenu processDefinition={currentProcessDefinition} />
                ) : null}
              </div>
              <div className="os-sb-left-nav-topitem">
                <ToggleServiceStatusMenu
                  serviceStatus={currentProcessDefinition?.status}
                  onChangeStatus={openChangeStatusFn}
                />
              </div>
              {toolbarItems}
            </LeftPanelToolbarNav>
          </Paper>
        </ThemeProvider>
      </Box>
    </>
  );
};

interface LeftPanelHeaderProps {
  serviceTitle: string;
  serviceLogo: string;
  isFetchingProcessDefinition: boolean;
}

const LeftPanelHeader = ({ serviceTitle, serviceLogo, isFetchingProcessDefinition }: LeftPanelHeaderProps) => {
  return (
    <ListItemButton component="a" href="#left-toolbar" className="os-service-builder-sidebar-header">
      <ListItemIcon sx={{ fontSize: 20 }}>
        {isFetchingProcessDefinition ? <CircularProgress /> : <OSAvatar name={serviceTitle} imageSrc={serviceLogo} />}
      </ListItemIcon>

      <ListItemText
        sx={{ my: 0 }}
        className="os-service-builder-sidebar-title"
        primary={serviceTitle}
        primaryTypographyProps={{
          fontSize: 16,
          fontWeight: "medium",
          letterSpacing: 0,
        }}
      />
    </ListItemButton>
  );
};

interface LeftPanelMenuProps {
  title: string;
  tooltipText: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const LeftPanelMenu = ({ title, onClick, tooltipText, icon }: LeftPanelMenuProps) => (
  <Tooltip title={tooltipText} placement="right" arrow>
    <ListItem component="div" disablePadding onClick={onClick}>
      <ListItemButton sx={{ height: 56 }} disableRipple>
        <ListItemText
          primary={title}
          primaryTypographyProps={{
            color: "rgb(166, 166, 166)",
            fontWeight: "medium",
            variant: "body2",
          }}
        />
        <div className="os-sb-left-nav-icon-action">{icon}</div>
      </ListItemButton>
    </ListItem>
  </Tooltip>
);
interface ServiceDetailsMenuProps {
  openProcessDefinitionGeneralInfoPopupFn: () => void;
}

const ServiceDetailsMenu = ({ openProcessDefinitionGeneralInfoPopupFn }: ServiceDetailsMenuProps) => (
  <LeftPanelMenu
    title="Edit Service Details"
    tooltipText="Change General Details"
    onClick={openProcessDefinitionGeneralInfoPopupFn}
    icon={<Settings sx={{ color: "rgb(166, 166, 166)" }} />}
  />
);

interface OpenStaffViewMenuProps {
  url: string;
}

const OpenStaffViewMenu = ({ url }: OpenStaffViewMenuProps) => {
  function openStaffDashboard() {
    window.open(url, "_blank").focus();
  }

  return (
    <>
      {url ? (
        <LeftPanelMenu
          title="Open in Portal"
          tooltipText="Open Service Dashboard in the portal to see how the service will appear for staff and/or applicants."
          onClick={openStaffDashboard}
          icon={<Launch sx={{ color: "rgb(166, 166, 166)" }} />}
        />
      ) : null}
    </>
  );
};

interface ShareSubmissionFormLinksMenuProps {
  processDefinition: ProcessDefinition;
}

const ShareFormLinksMenu = ({ processDefinition }: ShareSubmissionFormLinksMenuProps) => {
  return (
    <ShareServiceFormsLinksDialog processDefinition={processDefinition}>
      {({ handleOpen }) => (
        <LeftPanelMenu
          title="Share Form Links"
          tooltipText="Share form links for this service directly with your users or copy for use in advertising media."
          onClick={handleOpen}
          icon={<ShareIcon sx={{ color: "rgb(166, 166, 166)" }} />}
        />
      )}
    </ShareServiceFormsLinksDialog>
  );
};

interface ToggleServiceStatusMenuProps {
  serviceStatus: ProcessDefinition["status"];
  onChangeStatus: () => void;
}

const ToggleServiceStatusMenu = ({ serviceStatus, onChangeStatus }: ToggleServiceStatusMenuProps) => {
  let serviceStatusTitle: string;
  let serviceStatusTitleStyle: React.CSSProperties = { color: "rgb(166, 166, 166)" };
  switch (serviceStatus) {
    case "DEPLOYED":
      serviceStatusTitle = "Live Mode";
      serviceStatusTitleStyle = { color: StatusUIProperties?.DEPLOYED?.color, fontWeight: "bold" };
      break;
    case "PRE_DEPLOYMENT_TESTING":
      serviceStatusTitle = "Test Mode";
      serviceStatusTitleStyle = { color: StatusUIProperties?.PRE_DEPLOYMENT_TESTING?.color, fontWeight: "bold" };
      break;
    case "DEVELOPMENT":
      serviceStatusTitle = "Dev Mode";
      serviceStatusTitleStyle = { color: StatusUIProperties?.DEVELOPMENT?.color, fontWeight: "bold" };
      break;
    default:
      serviceStatusTitle = "Loading...";
  }

  return (
    <>
      <ListItem component="div" disablePadding onClick={() => {}}>
        <ListItemButton className="os-service-status-slider" sx={{ height: 56 }} disableRipple>
          <ListItemText
            primary={serviceStatusTitle}
            primaryTypographyProps={{
              fontWeight: "medium",
              variant: "body2",
              marginBottom: 1,
            }}
            sx={serviceStatusTitleStyle}
          />
          <div style={{ width: 80 }}>
            <ProcessDefinitionStatusSlider onChangeStatus={onChangeStatus} />
          </div>
        </ListItemButton>
      </ListItem>
    </>
  );
};
